import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import scheduleService from '../../../../services/community/scheduleService';
import connectionService from '../../../../services/community/connectionService';
import { ScheduleCommunityModel, ScheduleModel } from '../../../../models/schedule.model';
import { Event } from '../../../../types/event';
import { RootState } from '../../../../redux/reducers';
import { Connection, ConnectionType } from '../../../../redux/slices/connectionsSlice';
import Button from '../../../components/buttons/Button';
import Slider from '../../../components/misc/Slider';

type SelectedAssignee = {
  key: string;
  type: ConnectionType;
  label: string;
  userId?: number;
  externalMemberId?: number;
};

enum FastAssignStrategy {
  ROUND_ROBIN = 'ROUND_ROBIN',
  RANDOM_BALANCED = 'RANDOM_BALANCED',
}

enum FastAssignOverwriteMode {
  REPLACE = 'REPLACE',
  FILL_REMAINING = 'FILL_REMAINING',
}

enum FastAssignMode {
  MEMBERS = 'MEMBERS',
  COMMUNITIES = 'COMMUNITIES',
}

type SelectedCommunity = {
  communityId: number;
  label: string;
};

type FastAssignResult = {
  succeeded: number;
  failed: number;
  failedShiftTitles: string[];
};

type FastAssigningTabProps = {
  schedule: ScheduleModel;
  shifts: Event[];
  linkedCommunities?: ScheduleCommunityModel[];
  isMainBusinessCommunity?: boolean;
  onAssignmentsApplied: () => Promise<void>;
};

function getConnectionLabel(connection: Connection): string {
  return `${connection.fields.FIRST_NAME || ''} ${connection.fields.LAST_NAME || ''}`.trim();
}

function getConnectionSelectionKey(connection: Connection): string {
  return connection.type === ConnectionType.MEMBER
    ? `member-${connection.userId}`
    : `external-${connection.externalMemberId}`;
}

function mapConnectionToSelectedAssignee(connection: Connection): SelectedAssignee | null {
  if (connection.type === ConnectionType.MEMBER && connection.userId) {
    return {
      key: getConnectionSelectionKey(connection),
      type: ConnectionType.MEMBER,
      label: getConnectionLabel(connection),
      userId: connection.userId,
    };
  }

  if (connection.type === ConnectionType.EXTERNAL_MEMBER && connection.externalMemberId) {
    return {
      key: getConnectionSelectionKey(connection),
      type: ConnectionType.EXTERNAL_MEMBER,
      label: getConnectionLabel(connection),
      externalMemberId: connection.externalMemberId,
    };
  }

  return null;
}

function mapShiftAssignees(shift: Event): SelectedAssignee[] {
  return [
    ...shift.participants.map((participant) => ({
      key: `member-${participant.id}`,
      type: ConnectionType.MEMBER,
      label: `${participant.firstName} ${participant.lastName}`.trim(),
      userId: participant.id,
    })),
    ...shift.externalInvites.map((invite) => ({
      key: `external-${invite.externalMemberId}`,
      type: ConnectionType.EXTERNAL_MEMBER,
      label: `${invite.firstName} ${invite.lastName}`.trim(),
      externalMemberId: invite.externalMemberId,
    })),
  ];
}

function dedupeAssignees(assignees: SelectedAssignee[]): SelectedAssignee[] {
  const unique = new Map<string, SelectedAssignee>();
  assignees.forEach((assignee) => {
    unique.set(assignee.key, assignee);
  });
  return Array.from(unique.values());
}

function getDefaultShiftCapacity(shift: Event, fallback: number): number {
  if (typeof shift.maximumAttendees === 'number' && shift.maximumAttendees > 0) {
    return shift.maximumAttendees;
  }
  return fallback;
}

function shuffleAssignees(assignees: SelectedAssignee[]): SelectedAssignee[] {
  const items = [...assignees];
  for (let index = items.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
  }
  return items;
}

export default function FastAssigningTab({
  schedule,
  shifts,
  linkedCommunities = [],
  isMainBusinessCommunity = false,
  onAssignmentsApplied,
}: FastAssigningTabProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);
  const canAssignCommunities = isMainBusinessCommunity && linkedCommunities.length > 0;

  const [fastAssignMode, setFastAssignMode] = useState<FastAssignMode>(FastAssignMode.MEMBERS);

  const [fastAssignSelectedShiftIds, setFastAssignSelectedShiftIds] = useState<number[]>([]);
  const [fastAssignShiftSelectionInitialized, setFastAssignShiftSelectionInitialized] = useState(false);
  const [fastAssignStrategy, setFastAssignStrategy] = useState<FastAssignStrategy>(
    FastAssignStrategy.ROUND_ROBIN,
  );
  const [fastAssignOverwriteMode, setFastAssignOverwriteMode] = useState<FastAssignOverwriteMode>(
    FastAssignOverwriteMode.REPLACE,
  );
  const [fastAssignSelectedAssignees, setFastAssignSelectedAssignees] = useState<SelectedAssignee[]>([]);
  const [fastAssignShiftSearch, setFastAssignShiftSearch] = useState('');
  const [fastAssignAssigneeSearch, setFastAssignAssigneeSearch] = useState('');
  const [fastAssignAssigneeSelectionInitialized, setFastAssignAssigneeSelectionInitialized] =
    useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [fastAssigning, setFastAssigning] = useState(false);
  const [fastAssignResult, setFastAssignResult] = useState<FastAssignResult | null>(null);
  const [fastAssignSelectedCommunities, setFastAssignSelectedCommunities] = useState<SelectedCommunity[]>([]);
  const [fastAssignCommunitySearch, setFastAssignCommunitySearch] = useState('');
  const [fastAssignCommunitySelectionInitialized, setFastAssignCommunitySelectionInitialized] = useState(false);
  const [communityRequiredCount, setCommunityRequiredCount] = useState('1');

  const fastAssignStrategyDescription =
    fastAssignStrategy === FastAssignStrategy.ROUND_ROBIN
      ? t('page.event.schedules.fastAssigning.strategyDescriptions.roundRobin')
      : t('page.event.schedules.fastAssigning.strategyDescriptions.randomBalanced');

  const fastAssignOverwriteDescription =
    fastAssignOverwriteMode === FastAssignOverwriteMode.REPLACE
      ? t('page.event.schedules.fastAssigning.overwriteDescriptions.replace')
      : t('page.event.schedules.fastAssigning.overwriteDescriptions.fillRemaining');

  const fastAssignableConnections = useMemo(() => {
    return connections
      .filter((connection) => !!connection.active)
      .filter((connection) => {
        if (connection.type === ConnectionType.MEMBER) {
          return !!connection.userId;
        }

        return !!connection.externalMemberId;
      })
      .sort((a, b) => getConnectionLabel(a).localeCompare(getConnectionLabel(b)));
  }, [connections]);

  const filteredLinkedCommunities = useMemo(() => {
    const query = fastAssignCommunitySearch.trim().toLowerCase();

    return linkedCommunities
      .filter((community) => {
        if (!query) return true;
        return community.name.toLowerCase().includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [fastAssignCommunitySearch, linkedCommunities]);

  const parsedCommunityRequiredCount = Number(communityRequiredCount);
  const hasValidCommunityRequiredCount =
    Number.isInteger(parsedCommunityRequiredCount) && parsedCommunityRequiredCount > 0;

  const filteredFastAssignableConnections = useMemo(() => {
    const query = fastAssignAssigneeSearch.trim().toLowerCase();

    return fastAssignableConnections
      .filter((connection) => {
        if (!query) return true;
        return getConnectionLabel(connection).toLowerCase().includes(query);
      })
      .slice(0, 100);
  }, [fastAssignAssigneeSearch, fastAssignableConnections]);

  const selectedFastAssignShifts = useMemo(() => {
    const selectedShiftSet = new Set(fastAssignSelectedShiftIds);
    return shifts.filter((shift) => selectedShiftSet.has(shift.id));
  }, [fastAssignSelectedShiftIds, shifts]);

  const filteredShiftOptions = useMemo(() => {
    const query = fastAssignShiftSearch.trim().toLowerCase();

    return shifts
      .slice()
      .sort(
        (first, second) =>
          new Date(first.startTime).getTime() - new Date(second.startTime).getTime(),
      )
      .filter((shift) => {
        if (!query) return true;
        return shift.title.toLowerCase().includes(query);
      });
  }, [fastAssignShiftSearch, shifts]);

  useEffect(() => {
    if (connections.length === 0) {
      setLoadingConnections(true);
      connectionService.getConnections()
        .catch(() => {
          toast.error(t('general.error'));
        })
        .finally(() => {
          setLoadingConnections(false);
        });
    }
  }, [connections.length, t]);

  useEffect(() => {
    if (shifts.length === 0) {
      setFastAssignShiftSelectionInitialized(false);
      setFastAssignSelectedShiftIds([]);
      return;
    }

    if (!fastAssignShiftSelectionInitialized) {
      setFastAssignSelectedShiftIds(shifts.map((shift) => shift.id));
      setFastAssignShiftSelectionInitialized(true);
      return;
    }

    const availableShiftIds = new Set(shifts.map((shift) => shift.id));
    setFastAssignSelectedShiftIds((previous) => previous.filter((shiftId) => availableShiftIds.has(shiftId)));
  }, [fastAssignShiftSelectionInitialized, shifts]);

  useEffect(() => {
    if (fastAssignableConnections.length === 0) {
      setFastAssignAssigneeSelectionInitialized(false);
      setFastAssignSelectedAssignees([]);
      return;
    }

    if (!fastAssignAssigneeSelectionInitialized) {
      setFastAssignSelectedAssignees(
        fastAssignableConnections
          .map((connection) => mapConnectionToSelectedAssignee(connection))
          .filter((assignee): assignee is SelectedAssignee => assignee !== null),
      );
      setFastAssignAssigneeSelectionInitialized(true);
      return;
    }

    const availableKeys = new Set(
      fastAssignableConnections.map((connection) => getConnectionSelectionKey(connection)),
    );

    setFastAssignSelectedAssignees((previous) =>
      previous.filter((assignee) => availableKeys.has(assignee.key)),
    );
  }, [fastAssignAssigneeSelectionInitialized, fastAssignableConnections]);

  useEffect(() => {
    if (!canAssignCommunities || linkedCommunities.length === 0) {
      setFastAssignCommunitySelectionInitialized(false);
      setFastAssignSelectedCommunities([]);
      return;
    }

    if (!fastAssignCommunitySelectionInitialized) {
      setFastAssignSelectedCommunities(
        linkedCommunities.map((community) => ({
          communityId: community.communityId,
          label: community.name,
        })),
      );
      setFastAssignCommunitySelectionInitialized(true);
      return;
    }

    const availableIds = new Set(linkedCommunities.map((community) => community.communityId));
    setFastAssignSelectedCommunities((previous) =>
      previous.filter((community) => availableIds.has(community.communityId)),
    );
  }, [canAssignCommunities, fastAssignCommunitySelectionInitialized, linkedCommunities]);

  const toggleFastAssignShiftSelection = (shiftId: number) => {
    setFastAssignSelectedShiftIds((previous) => {
      if (previous.includes(shiftId)) {
        return previous.filter((id) => id !== shiftId);
      }
      return [...previous, shiftId];
    });
  };

  const selectAllFastAssignShifts = () => {
    setFastAssignSelectedShiftIds(shifts.map((shift) => shift.id));
  };

  const clearFastAssignShifts = () => {
    setFastAssignSelectedShiftIds([]);
  };

  const toggleFastAssignAssigneeSelection = (connection: Connection) => {
    const key = getConnectionSelectionKey(connection);
    const selectedAssignee = mapConnectionToSelectedAssignee(connection);

    if (!selectedAssignee) {
      return;
    }

    setFastAssignSelectedAssignees((previous) => {
      const alreadySelected = previous.some((assignee) => assignee.key === key);
      if (alreadySelected) {
        return previous.filter((assignee) => assignee.key !== key);
      }

      return dedupeAssignees([...previous, selectedAssignee]);
    });
  };

  const selectAllFastAssignAssignees = () => {
    const allAssignees = fastAssignableConnections
      .map((connection) => mapConnectionToSelectedAssignee(connection))
      .filter((assignee): assignee is SelectedAssignee => assignee !== null);
    setFastAssignSelectedAssignees(dedupeAssignees(allAssignees));
  };

  const clearFastAssignAssignees = () => {
    setFastAssignSelectedAssignees([]);
  };

  const toggleFastAssignCommunitySelection = (community: ScheduleCommunityModel) => {
    setFastAssignSelectedCommunities((previous) => {
      const exists = previous.some((entry) => entry.communityId === community.communityId);
      if (exists) {
        return previous.filter((entry) => entry.communityId !== community.communityId);
      }

      return [...previous, { communityId: community.communityId, label: community.name }];
    });
  };

  const selectAllFastAssignCommunities = () => {
    setFastAssignSelectedCommunities(
      linkedCommunities.map((community) => ({
        communityId: community.communityId,
        label: community.name,
      })),
    );
  };

  const clearFastAssignCommunities = () => {
    setFastAssignSelectedCommunities([]);
  };

  const calculateShiftAssignments = (): Map<number, SelectedAssignee[]> => {
    const assignmentPlan = new Map<number, SelectedAssignee[]>();

    if (selectedFastAssignShifts.length === 0 || fastAssignSelectedAssignees.length === 0) {
      return assignmentPlan;
    }

    const sortedShifts = [...selectedFastAssignShifts].sort((a, b) => {
      const dateDifference = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      if (dateDifference !== 0) {
        return dateDifference;
      }
      return a.id - b.id;
    });

    const basePool =
      fastAssignStrategy === FastAssignStrategy.RANDOM_BALANCED
        ? shuffleAssignees(fastAssignSelectedAssignees)
        : [...fastAssignSelectedAssignees];

    let roundRobinCursor = 0;

    const pickRoundRobinAssignees = (
      count: number,
      excludedKeys: Set<string>,
      preferredOrder: SelectedAssignee[] = basePool,
    ): SelectedAssignee[] => {
      if (count <= 0 || preferredOrder.length === 0) {
        return [];
      }

      const selectedForShift: SelectedAssignee[] = [];
      let attempts = 0;
      const maxAttempts = preferredOrder.length * 3;

      while (selectedForShift.length < count && attempts < maxAttempts) {
        const candidate = preferredOrder[roundRobinCursor % preferredOrder.length];
        roundRobinCursor += 1;
        attempts += 1;

        const isExcluded = excludedKeys.has(candidate.key);
        const alreadySelected = selectedForShift.some((assignee) => assignee.key === candidate.key);

        if (!isExcluded && !alreadySelected) {
          selectedForShift.push(candidate);
        }
      }

      return selectedForShift;
    };

    sortedShifts.forEach((shift) => {
      const existingAssignees = mapShiftAssignees(shift);
      const existingSet = new Set(existingAssignees.map((assignee) => assignee.key));
      const capacity = getDefaultShiftCapacity(shift, basePool.length + existingAssignees.length);
      const remainingSpots =
        fastAssignOverwriteMode === FastAssignOverwriteMode.REPLACE
          ? capacity
          : Math.max(0, capacity - existingAssignees.length);

      let additions: SelectedAssignee[] = [];
      additions = pickRoundRobinAssignees(remainingSpots, existingSet, basePool);

      assignmentPlan.set(
        shift.id,
        fastAssignOverwriteMode === FastAssignOverwriteMode.REPLACE
          ? additions
          : dedupeAssignees([...existingAssignees, ...additions]),
      );
    });

    return assignmentPlan;
  };

  const calculateCommunityAssignments = (): Map<number, { communityId: number; requiredCount: number }[]> => {
    const assignmentPlan = new Map<number, { communityId: number; requiredCount: number }[]>();

    if (
      selectedFastAssignShifts.length === 0 ||
      fastAssignSelectedCommunities.length === 0 ||
      !hasValidCommunityRequiredCount
    ) {
      return assignmentPlan;
    }

    const sortedShifts = [...selectedFastAssignShifts].sort((a, b) => {
      const dateDifference = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      if (dateDifference !== 0) {
        return dateDifference;
      }
      return a.id - b.id;
    });

    const basePool =
      fastAssignStrategy === FastAssignStrategy.RANDOM_BALANCED
        ? shuffleAssignees(
            fastAssignSelectedCommunities.map((community) => ({
              key: `community-${community.communityId}`,
              type: ConnectionType.MEMBER,
              label: community.label,
            })),
          ).map((entry) => Number(entry.key.replace('community-', '')))
        : fastAssignSelectedCommunities.map((community) => community.communityId);

    let roundRobinCursor = 0;

    sortedShifts.forEach((shift) => {
      const existingAssignments = shift.communityAssignments ?? [];
      const capacity = getDefaultShiftCapacity(shift, parsedCommunityRequiredCount);
      const targetCapacity =
        fastAssignOverwriteMode === FastAssignOverwriteMode.REPLACE
          ? capacity
          : Math.max(0, capacity - existingAssignments.reduce((sum, entry) => sum + entry.requiredCount, 0));

      if (targetCapacity <= 0) {
        if (fastAssignOverwriteMode === FastAssignOverwriteMode.FILL_REMAINING && existingAssignments.length > 0) {
          assignmentPlan.set(
            shift.id,
            existingAssignments.map((entry) => ({
              communityId: entry.communityId,
              requiredCount: entry.requiredCount,
            })),
          );
        }
        return;
      }

      const nextCommunityId = basePool[roundRobinCursor % basePool.length];
      roundRobinCursor += 1;
      const requiredCount = Math.min(parsedCommunityRequiredCount, targetCapacity);

      const nextAssignments =
        fastAssignOverwriteMode === FastAssignOverwriteMode.REPLACE
          ? [{ communityId: nextCommunityId, requiredCount }]
          : [
              ...existingAssignments.map((entry) => ({
                communityId: entry.communityId,
                requiredCount: entry.requiredCount,
              })),
              { communityId: nextCommunityId, requiredCount },
            ];

      assignmentPlan.set(shift.id, nextAssignments);
    });

    return assignmentPlan;
  };

  const handleApplyFastAssignment = async () => {
    if (selectedFastAssignShifts.length === 0) {
      toast.error(t('page.event.schedules.fastAssigning.validation.selectShifts'));
      return;
    }

    if (fastAssignMode === FastAssignMode.COMMUNITIES) {
      if (fastAssignSelectedCommunities.length === 0) {
        toast.error(t('page.event.schedules.fastAssigning.validation.selectCommunities'));
        return;
      }

      if (!hasValidCommunityRequiredCount) {
        toast.error(t('page.event.schedules.fastAssigning.validation.invalidRequiredCount'));
        return;
      }

      try {
        setFastAssigning(true);
        setFastAssignResult(null);

        const assignmentPlan = calculateCommunityAssignments();
        const assignmentRequests = selectedFastAssignShifts.map(async (shift) => {
          const assignments = assignmentPlan.get(shift.id) ?? [];

          await scheduleService.setShiftCommunityAssignments(schedule.scheduleId, shift.id, {
            assignments,
          });

          return shift.title;
        });

        const settledResults = await Promise.allSettled(assignmentRequests);
        const succeeded = settledResults.filter((result) => result.status === 'fulfilled').length;
        const failedShiftTitles = settledResults
          .map((result, index) => (result.status === 'rejected' ? selectedFastAssignShifts[index].title : null))
          .filter((title): title is string => !!title);
        const failed = failedShiftTitles.length;

        setFastAssignResult({
          succeeded,
          failed,
          failedShiftTitles,
        });

        await onAssignmentsApplied();

        if (failed === 0) {
          toast.success(t('page.event.schedules.fastAssigning.messages.success'));
        } else {
          toast.error(t('page.event.schedules.fastAssigning.messages.partialError'));
        }
      } finally {
        setFastAssigning(false);
      }

      return;
    }

    if (fastAssignSelectedAssignees.length === 0) {
      toast.error(t('page.event.schedules.fastAssigning.validation.selectMembers'));
      return;
    }

    try {
      setFastAssigning(true);
      setFastAssignResult(null);

      const assignmentPlan = calculateShiftAssignments();

      const assignmentRequests = selectedFastAssignShifts.map(async (shift) => {
        const targetAssignees = assignmentPlan.get(shift.id) ?? [];

        await scheduleService.setShiftAssignments(schedule.scheduleId, shift.id, {
          participantsIds: targetAssignees
            .filter((assignee) => assignee.type === ConnectionType.MEMBER && assignee.userId)
            .map((assignee) => assignee.userId as number),
          externalInvites: targetAssignees
            .filter(
              (assignee) =>
                assignee.type === ConnectionType.EXTERNAL_MEMBER && assignee.externalMemberId,
            )
            .map((assignee) => ({ externalMemberId: assignee.externalMemberId as number })),
        });

        return shift.title;
      });

      const settledResults = await Promise.allSettled(assignmentRequests);
      const succeeded = settledResults.filter((result) => result.status === 'fulfilled').length;
      const failedShiftTitles = settledResults
        .map((result, index) => (result.status === 'rejected' ? selectedFastAssignShifts[index].title : null))
        .filter((title): title is string => !!title);
      const failed = failedShiftTitles.length;

      setFastAssignResult({
        succeeded,
        failed,
        failedShiftTitles,
      });

      await onAssignmentsApplied();

      if (failed === 0) {
        toast.success(t('page.event.schedules.fastAssigning.messages.success'));
      } else {
        toast.error(t('page.event.schedules.fastAssigning.messages.partialError'));
      }
    } finally {
      setFastAssigning(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-primary-900">{t('page.event.schedules.fastAssigning.title')}</h3>
        <p className="text-sm text-primary-500">{t('page.event.schedules.fastAssigning.subtitle')}</p>
      </div>

      {canAssignCommunities && (
        <Slider
          className="w-full max-w-[560px]"
          label={t('page.event.schedules.fastAssigning.mode')}
          items={[
            {
              label: t('page.event.schedules.fastAssigning.modes.members'),
              value: FastAssignMode.MEMBERS,
            },
            {
              label: t('page.event.schedules.fastAssigning.modes.communities'),
              value: FastAssignMode.COMMUNITIES,
            },
          ]}
          value={fastAssignMode}
          onChange={(value) => setFastAssignMode(value as FastAssignMode)}
        />
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="flex flex-col max-h-[500px] gap-3 rounded-xl border border-secondary-200 bg-secondary-200 bg-opacity-30 p-4">
          <div className="flex items-center justify-between">
            <p className="font-medium text-primary-900">{t('page.event.schedules.fastAssigning.shiftSelection')}</p>
            <div className="flex gap-2">
              <Button variant="tertiary" onClick={selectAllFastAssignShifts}>
                {t('page.event.schedules.fastAssigning.selectAll')}
              </Button>
              <Button variant="tertiary" onClick={clearFastAssignShifts}>
                {t('page.event.schedules.fastAssigning.clear')}
              </Button>
            </div>
          </div>

          <input
            value={fastAssignShiftSearch}
            onChange={(event) => setFastAssignShiftSearch(event.target.value)}
            placeholder={t('page.event.schedules.fastAssigning.searchShifts')}
            className="w-full rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary-900 focus:outline-none focus:ring-0"
          />

          <div className="overflow-auto rounded-xl border border-secondary-200 bg-secondary-50">
            {filteredShiftOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-primary-500">
                {shifts.length === 0
                  ? t('page.event.schedules.emptyShifts')
                  : t('page.event.schedules.fastAssigning.noShiftsFound')}
              </div>
            ) : (
              filteredShiftOptions.map((shift) => {
                  const checked = fastAssignSelectedShiftIds.includes(shift.id);
                  const assignedCount = shift.participants.length + shift.externalInvites.length;

                return (
                  <label
                    key={shift.id}
                    className="flex cursor-pointer items-start justify-between gap-3 border-b border-secondary-200 px-3 py-2 last:border-b-0 hover:bg-secondary-200 hover:bg-opacity-40">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFastAssignShiftSelection(shift.id)}
                        className="mt-1"
                      />
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-primary-900">{shift.title}</span>
                        <span className="text-xs text-primary-500">
                          {new Date(shift.startTime).toLocaleString(i18n.language)}
                        </span>
                      </span>
                    </div>
                    <span className="text-xs text-primary-500">
                      {assignedCount}
                      {shift.maximumAttendees ? ` / ${shift.maximumAttendees}` : ''}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 max-h-[500px] rounded-xl border border-secondary-200 bg-secondary-200 bg-opacity-30 p-4">
          <div className="flex items-center justify-between">
            <p className="font-medium text-primary-900">
              {fastAssignMode === FastAssignMode.COMMUNITIES
                ? t('page.event.schedules.fastAssigning.communitySelection')
                : t('page.event.schedules.fastAssigning.memberPool')}
            </p>
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                onClick={
                  fastAssignMode === FastAssignMode.COMMUNITIES
                    ? selectAllFastAssignCommunities
                    : selectAllFastAssignAssignees
                }>
                {t('page.event.schedules.fastAssigning.selectAll')}
              </Button>
              <Button
                variant="tertiary"
                onClick={
                  fastAssignMode === FastAssignMode.COMMUNITIES
                    ? clearFastAssignCommunities
                    : clearFastAssignAssignees
                }>
                {t('page.event.schedules.fastAssigning.clear')}
              </Button>
            </div>
          </div>

          {fastAssignMode === FastAssignMode.COMMUNITIES ? (
            <>
              <input
                value={fastAssignCommunitySearch}
                onChange={(event) => setFastAssignCommunitySearch(event.target.value)}
                placeholder={t('page.event.schedules.fastAssigning.searchCommunities')}
                className="w-full rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary-900 focus:outline-none focus:ring-0"
              />
              <input
                value={communityRequiredCount}
                onChange={(event) => setCommunityRequiredCount(event.target.value)}
                placeholder={t('page.event.schedules.fastAssigning.requiredPeoplePerCommunity')}
                className="w-full rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary-900 focus:outline-none focus:ring-0"
              />
              <div className="overflow-auto rounded-xl border border-secondary-200 bg-secondary-50">
                {filteredLinkedCommunities.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-primary-500">
                    {t('page.event.schedules.fastAssigning.noCommunitiesFound')}
                  </div>
                ) : (
                  filteredLinkedCommunities.map((community) => {
                    const checked = fastAssignSelectedCommunities.some(
                      (entry) => entry.communityId === community.communityId,
                    );

                    return (
                      <label
                        key={community.communityId}
                        className="flex cursor-pointer items-start justify-between gap-3 border-b border-secondary-200 px-3 py-2 last:border-b-0 hover:bg-secondary-200 hover:bg-opacity-40">
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFastAssignCommunitySelection(community)}
                            className="mt-1"
                          />
                          <span className="text-sm font-medium text-primary-900">{community.name}</span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
          <input
            value={fastAssignAssigneeSearch}
            onChange={(event) => setFastAssignAssigneeSearch(event.target.value)}
            placeholder={t('page.event.schedules.fastAssigning.searchMembers')}
            className="w-full rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-primary-900 focus:outline-none focus:ring-0"
          />

          <div className="overflow-auto rounded-xl border border-secondary-200 bg-secondary-50">
            {loadingConnections ? (
              <div className="px-3 py-2 text-sm text-primary-500">{t('general.loading')}</div>
            ) : filteredFastAssignableConnections.length === 0 ? (
              <div className="px-3 py-2 text-sm text-primary-500">{t('page.event.schedules.noMembersFound')}</div>
            ) : (
              filteredFastAssignableConnections.map((connection) => {
                const key = getConnectionSelectionKey(connection);
                const checked = fastAssignSelectedAssignees.some((assignee) => assignee.key === key);

                return (
                  <label
                    key={key}
                    className="flex cursor-pointer items-start justify-between gap-3 border-b border-secondary-200 px-3 py-2 last:border-b-0 hover:bg-secondary-200 hover:bg-opacity-40">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFastAssignAssigneeSelection(connection)}
                        className="mt-1"
                      />
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-primary-900">
                          {getConnectionLabel(connection)}
                        </span>
                        <span className="text-xs text-primary-500">
                          {connection.type === ConnectionType.EXTERNAL_MEMBER
                            ? t('page.event.schedules.fastAssigning.external')
                            : t('page.event.schedules.fastAssigning.member')}
                        </span>
                      </span>
                    </div>
                  </label>
                );
              })
            )}
          </div>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Slider
            className="w-full max-w-[560px]"
            label={t('page.event.schedules.fastAssigning.strategy')}
            items={[
              {
                label: t('page.event.schedules.fastAssigning.strategies.roundRobin'),
                value: FastAssignStrategy.ROUND_ROBIN,
              },
              {
                label: t('page.event.schedules.fastAssigning.strategies.randomBalanced'),
                value: FastAssignStrategy.RANDOM_BALANCED,
              },
            ]}
            value={fastAssignStrategy}
            onChange={(value) => setFastAssignStrategy(value as FastAssignStrategy)}
          />
          <p className="text-xs text-primary-500">{fastAssignStrategyDescription}</p>
        </div>

        <div className="flex flex-col gap-1">
          <Slider
            className="w-full max-w-[560px]"
            label={t('page.event.schedules.fastAssigning.overwriteMode')}
            items={[
              {
                label: t('page.event.schedules.fastAssigning.overwrite.replace'),
                value: FastAssignOverwriteMode.REPLACE,
              },
              {
                label: t('page.event.schedules.fastAssigning.overwrite.fillRemaining'),
                value: FastAssignOverwriteMode.FILL_REMAINING,
              },
            ]}
            value={fastAssignOverwriteMode}
            onChange={(value) => setFastAssignOverwriteMode(value as FastAssignOverwriteMode)}
          />
          <p className="text-xs text-primary-500">{fastAssignOverwriteDescription}</p>
        </div>
      </div>

      <div className="flex">
        <Button
          variant="primary"
          onClick={handleApplyFastAssignment}
          disabled={
            fastAssigning ||
            selectedFastAssignShifts.length === 0 ||
            (fastAssignMode === FastAssignMode.COMMUNITIES
              ? fastAssignSelectedCommunities.length === 0 || !hasValidCommunityRequiredCount
              : fastAssignSelectedAssignees.length === 0)
          }>
          {fastAssigning
            ? t('page.event.schedules.fastAssigning.applying')
            : t('page.event.schedules.fastAssigning.apply')}
        </Button>
      </div>
    </div>
  );
}
